/**
 * Data + navigation behind the entry-page search (P6-03 / P6-04 / P5-11).
 *
 * Static entries (pages from both sidebars filtered by access, help entries, intents) are built at
 * once; dynamic sources load lazily on the first real query and are cached for the session:
 * case studies (+ extracted text from `Hugo_DocumentText`, P5-08), the org's surveys, OE
 * Conditions in the viewer's scope, and members (only when the viewer may View As — admins).
 * Every submitted query is logged to `Hugo_ActivityLog` (`action: 'search'`) so the later AI
 * routing (§8 "master assistant") has real phrasing to learn from — no PII beyond the query.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useDemoRole } from '../../contexts/DemoRoleContext';
import { repsitSidebar, drewSidebar, filterSidebar } from '../navigation/navigation.js';
import { canAccessToolPath } from '../../assistants/drew/utils/accessControl';
import { FEATURE_HUGO } from '../../config/features';
import { HELP_ENTRIES, helpSlug } from '../../pages/helpContent';
import { getVisibleCaseStudies, getDocumentTexts } from '../../services/documentService';
import { getSurveysForOrganization } from '../../services/surveyService';
import { listTRLs } from '../../services/trlService';
import { logActivity } from '../../utils/firestore';
import { openFeedback } from '../../config/feedback';
import {
  searchIndex, pageEntries, helpEntries, caseStudyEntries, surveyEntries, conditionEntries, memberEntries, intentEntries,
} from './searchIndex.js';

const docIdOf = (cs) => cs.sourceDocumentId || (typeof cs.id === 'string' && cs.id.startsWith('cs_') ? cs.id.slice(3) : null);

export function useGlobalSearch({ organizationId } = {}) {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();
  const { persona, queryScope, canViewAs, members } = useDemoRole();
  const personaId = persona?.id;

  const staticEntries = useMemo(() => {
    const can = (path) => canAccessToolPath(personaId, path);
    const repsit = filterSidebar(repsitSidebar({ isSuperAdmin: isSuperAdmin?.() }), can);
    const drew = filterSidebar(drewSidebar({ featureHugo: FEATURE_HUGO }), can);
    return [...intentEntries(), ...pageEntries(repsit, { group: 'platform' }), ...pageEntries(drew, { group: 'drew' }), ...helpEntries(HELP_ENTRIES, { helpSlug })];
  }, [personaId, isSuperAdmin]);

  const [dynamic, setDynamic] = useState({ caseStudies: [], fullText: {}, surveys: [], conditions: [] });
  const [loading, setLoading] = useState(false);
  const loadedRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  const ensureLoaded = useCallback(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    setLoading(true);
    const orgId = organizationId || queryScope?.organizationId || null;
    const tasks = [
      getVisibleCaseStudies(orgId).then((all) => all.filter((cs) => ['ready', 'published'].includes(cs.status) || !cs.status)).catch(() => []),
      orgId ? getSurveysForOrganization(orgId).catch(() => []) : Promise.resolve([]),
      orgId ? listTRLs({ ...(queryScope || {}), organizationId: orgId }, { limit: 200 }).catch(() => []) : Promise.resolve([]),
    ];
    Promise.all(tasks)
      .then(async ([caseStudies, surveys, conditions]) => {
        let fullText = {};
        try {
          const byDoc = await getDocumentTexts(caseStudies.map(docIdOf).filter(Boolean));
          for (const cs of caseStudies) { const d = docIdOf(cs); if (d && byDoc[d]) fullText[cs.id] = byDoc[d]; }
        } catch { /* index optional */ }
        if (mountedRef.current) setDynamic({ caseStudies, fullText, surveys, conditions });
      })
      .finally(() => { if (mountedRef.current) setLoading(false); });
  }, [organizationId, queryScope]);

  const entries = useMemo(() => [
    ...staticEntries,
    ...caseStudyEntries(dynamic.caseStudies, dynamic.fullText),
    ...surveyEntries(dynamic.surveys),
    ...conditionEntries(dynamic.conditions),
    ...(canViewAs ? memberEntries(members) : []),
  ], [staticEntries, dynamic, canViewAs, members]);

  const search = useCallback((query) => searchIndex(entries, query), [entries]);

  const logQuery = useCallback((query, result) => {
    if (!user?.uid || !query?.trim()) return;
    logActivity(user.uid, 'search', 'repsit', {
      query: query.trim().slice(0, 200),
      resultCount: result?.results?.length ?? 0,
      best: result?.best ? { kind: result.best.kind, id: result.best.id } : null,
      organizationId: organizationId || queryScope?.organizationId || null,
    });
  }, [user?.uid, organizationId, queryScope]);

  /** Open a result (or the best match for a query). Returns true when something happened. */
  const open = useCallback((entry, { query } = {}) => {
    if (!entry) return false;
    if (entry.action === 'feedback') { openFeedback('feedback', { message: query || '' }); return true; }
    if (!entry.path) return false;
    const q = query && entry.kind === 'caseStudy' ? `${entry.path.includes('?') ? '&' : '?'}q=${encodeURIComponent(query)}` : '';
    navigate(`${entry.path}${q}`);
    return true;
  }, [navigate]);

  return { entries, search, ensureLoaded, loading, open, logQuery };
}

export default useGlobalSearch;
